/**
 * Renders one procedural tree per side margin — rooted at the bottom,
 * thick tapered trunk (bare), a few thin forking branches that taper to
 * points, each tipped with one small simple leaf — as a single fixed,
 * translucent SVG overlay behind the studio content. On
 * first paint the tree fades in as one unit so it reads as growing up
 * from the base.
 *
 * Colour is a single warm tone-on-tone hue driven by the `--branch-wood`
 * / `--branch-leaf` CSS variables (defined per theme in studio.css), so
 * the field re-tints automatically on light ↔ dark.
 *
 * `getPerchPoints()` exposes the thick, near-horizontal stretch of each
 * branch (lifted just above the wood) so the bird has a comfortable
 * place to nest. On narrow viewports the tree compresses into the right
 * edge and sits behind the interface instead of disappearing. Perch
 * targets are exposed only when the bird's full footprint clears every
 * useful Studio surface.
 */

import { generateField, type FieldGeometry, type PerchPoint } from "./generate";
import { FOLIAGE_MARKUP } from "./foliageAsset";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Width of the clean right-hand strip reserved for the tree. The layout
 * keeps panels out of this band (see `.studio-workspace` padding-right in
 * studio.css) so the silhouette has free space. Keep these two values in
 * sync.
 */
const RIGHT_GUTTER_PX = 200;
/**
 * When the workspace cannot reserve the full gutter, compress the tree
 * into a smaller edge band. BranchField remains behind the functional UI,
 * while studio.css softly masks its inward reach for text legibility.
 */
const MIN_RIGHT_GUTTER_PX = 84;
const RESPONSIVE_GUTTER_FRACTION = 0.16;
const BIRD_FOOTPRINT_PX = 96;
const PERCH_CLEARANCE_PX = 12;
const PROTECTED_SURFACE_SELECTORS = [
  ".studio-top-strip",
  ".studio-sidebar",
  ".studio-script-dock:not(.is-closed)",
  ".studio-panel-host",
  ".studio-timeline-dock",
  '.studio-modal-root[data-open="true"]',
  '.studio-drawer-root[data-open="true"]',
] as const;

/**
 * Intro: the trees "grow" up from the ground — a clip wipe revealing the
 * silhouette bottom-to-top — with a quick opacity fade so the leading
 * edge stays soft. Runs once on first paint; resizes snap (see `render`).
 */
const GROW_IN_MS = 1400;
const FADE_IN_MS = 700;
const RESIZE_DEBOUNCE_MS = 200;
/** Matches studio.css `--ease-quart` for a soft, settling motion. */
const GROW_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

export interface BranchFieldOptions {
  /** Seed for a reproducible layout. Random per load if omitted. */
  seed?: number;
}

export class BranchField {
  private readonly host: HTMLElement;
  private readonly baseSeed: number;
  private readonly svg: SVGSVGElement;
  private readonly prefersReducedMotion: boolean;

  private perchPoints: PerchPoint[] = [];
  private resizeTimerId: number | null = null;
  private lastWidth = 0;
  private lastHeight = 0;

  constructor(host: HTMLElement, options: BranchFieldOptions = {}) {
    this.host = host;
    this.baseSeed = (options.seed ?? Math.floor(Date.now() % 2147483647)) >>> 0;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.svg = document.createElementNS(SVG_NS, "svg");
    this.svg.classList.add("koko-branch-field");
    this.svg.setAttribute("aria-hidden", "true");
    Object.assign(this.svg.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      // Functional UI always wins the stacking order. The tree remains
      // visible in reserved gutters and transparent negative space, never
      // across cards, controls, or working text.
      zIndex: "0",
    } satisfies Partial<CSSStyleDeclaration>);
    this.host.appendChild(this.svg);

    window.addEventListener("resize", this.handleResize, { passive: true });

    // First render animates; resizes snap (see `render`).
    this.render(true);
  }

  /**
   * Perch targets in viewport coordinates. A point is returned only when
   * Koko's full 96px body plus breathing room clears all useful surfaces.
   * An empty list is intentional: on a crowded layout, Koko should stay
   * awake instead of falling back to a corner over the interface.
   */
  getPerchPoints(): PerchPoint[] {
    const protectedRects = PROTECTED_SURFACE_SELECTORS.flatMap((selector) =>
      Array.from(document.querySelectorAll<HTMLElement>(selector))
        .filter((element) => this.isVisible(element))
        .map((element) => element.getBoundingClientRect()),
    );
    if (protectedRects.length === 0) return this.perchPoints;

    const halfFootprint = BIRD_FOOTPRINT_PX / 2 + PERCH_CLEARANCE_PX;
    return this.perchPoints.filter((point) => {
      const birdRect = {
        left: point.viewportX - halfFootprint,
        right: point.viewportX + halfFootprint,
        top: point.viewportY - halfFootprint,
        bottom: point.viewportY + halfFootprint,
      };
      return protectedRects.every(
        (surface) =>
          birdRect.right <= surface.left ||
          birdRect.left >= surface.right ||
          birdRect.bottom <= surface.top ||
          birdRect.top >= surface.bottom,
      );
    });
  }

  destroy(): void {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimerId !== null) window.clearTimeout(this.resizeTimerId);
    this.svg.remove();
  }

  private handleResize = (): void => {
    if (this.resizeTimerId !== null) window.clearTimeout(this.resizeTimerId);
    this.resizeTimerId = window.setTimeout(() => {
      this.resizeTimerId = null;
      if (window.innerWidth === this.lastWidth && window.innerHeight === this.lastHeight) {
        return;
      }
      this.render(false);
    }, RESIZE_DEBOUNCE_MS);
  };

  private isVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  }

  private render(animate: boolean): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.lastWidth = width;
    this.lastHeight = height;

    this.svg.replaceChildren();
    this.svg.setAttribute("width", String(width));
    this.svg.setAttribute("height", String(height));

    this.svg.style.display = "";

    const marginWidth = Math.min(
      RIGHT_GUTTER_PX,
      Math.max(MIN_RIGHT_GUTTER_PX, width * RESPONSIVE_GUTTER_FRACTION),
    );

    const field = generateField({
      width,
      height,
      seed: this.baseSeed,
      marginWidth,
    });
    this.paint(field, animate && !this.prefersReducedMotion);
    this.perchPoints = field.perchPoints;
  }

  private paint(field: FieldGeometry, animate: boolean): void {
    // One wrapper group holds the whole tree. Fading it as a unit keeps
    // the silhouette flat throughout the intro — no per-part overlap
    // compositing. The flat tone-on-tone opacity lives on the SVG (CSS).
    const tree = document.createElementNS(SVG_NS, "g");
    const limbGroup = document.createElementNS(SVG_NS, "g");
    const leafGroup = document.createElementNS(SVG_NS, "g");

    for (const limb of field.limbs) {
      limbGroup.appendChild(this.makePath(limb.d, "koko-limb"));
    }
    const template = this.foliageTemplate();
    for (const sprig of field.foliage) {
      const g = document.createElementNS(SVG_NS, "g");
      g.setAttribute("class", "koko-foliage");
      g.setAttribute("transform", sprig.transform);
      g.appendChild(template.cloneNode(true));
      leafGroup.appendChild(g);
    }

    // Leaves above the wood so foliage reads in front of the branches.
    tree.appendChild(limbGroup);
    tree.appendChild(leafGroup);
    this.svg.appendChild(tree);

    if (animate) {
      // Grow up from the ground: wipe the clip from fully-clipped (bottom)
      // to fully-revealed (top). The fade keeps the leading edge soft.
      this.svg.animate(
        [{ clipPath: "inset(100% 0 0 0)" }, { clipPath: "inset(0% 0 0 0)" }],
        { duration: GROW_IN_MS, easing: GROW_EASING, fill: "backwards" },
      );
      tree.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: FADE_IN_MS, easing: GROW_EASING, fill: "backwards" },
      );
    }
  }

  private parsedFoliage: Element | null = null;

  /** Parse the leaf markup once (as SVG), then clone per instance. */
  private foliageTemplate(): Element {
    if (!this.parsedFoliage) {
      const doc = new DOMParser().parseFromString(
        `<svg xmlns="${SVG_NS}">${FOLIAGE_MARKUP}</svg>`,
        "image/svg+xml",
      );
      this.parsedFoliage = doc.documentElement.firstElementChild!;
    }
    return this.parsedFoliage;
  }

  private makePath(d: string, className: string): SVGPathElement {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("class", className);
    return path;
  }
}
